import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { SelectList } from "@common/selectList";
import { Calendar } from "@common/calendar";
import { Props } from "./types";
import styles from "./styles.scss";

// 96-entry 15-minute lookup, module scope so it's not re-created per render.
const times = Object.fromEntries(
  Array.from({ length: 96 }, (_, i) => {
    const hours = Math.floor(i / 4);
    const minutes = (i % 4) * 15;
    return [`${hours}h${String(minutes).padStart(2, "0")}`, { hours, minutes }];
  }),
);

function DateTimePicker({
  dateTime,
  projectTimezone,
  placeholder,
  isInvalid,
  readOnly,
  minDate,
  maxDate,
  initialCalendarDate,
  input,
  clear,
  today,
  selectAllDay,
  setSelectAllDay,
  isEndDate,
}: Props) {
  const { t } = useTranslation();
  const zone = projectTimezone || DateTime.local().zoneName;
  const display = dateTime
    ? DateTime.fromISO(dateTime, { zone: "utc" }).setZone(zone)
    : null;
  const isValid = !!display && display.isValid;

  // When selectAllDay overrides the time, display 00:00 (start) or 23:59
  // (end) regardless of the stored value. Clicking a specific time in the
  // dropdown calls setSelectAllDay(false) to exit all-day mode.
  const effectiveTimeText = selectAllDay
    ? isEndDate
      ? "23:59"
      : "00:00"
    : isValid
      ? display.toFormat("HH:mm")
      : "00:00";

  const [timeText, setTimeText] = React.useState(effectiveTimeText);

  React.useEffect(() => {
    setTimeText(effectiveTimeText);
  }, [effectiveTimeText]);

  const commitDateTime = (
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
  ) => {
    const next = DateTime.fromObject(
      { year, month, day, hour, minute, second: 0, millisecond: 0 },
      { zone },
    );
    if (!next.isValid) return "";
    return next.toUTC().toISO() ?? "";
  };

  const commitTime = (text: string) => {
    const match = text.match(/^(\d{1,2}):(\d{2})$/);
    const h = match ? parseInt(match[1], 10) : NaN;
    const m = match ? parseInt(match[2], 10) : NaN;
    if (!match || h > 23 || m > 59) {
      setTimeText(effectiveTimeText);
      return;
    }
    const base = isValid ? display : DateTime.now().setZone(zone);
    const iso = commitDateTime(base.year, base.month, base.day, h, m);
    if (iso) input(iso);
  };

  // Calendar widget's initial focus date when no value is selected.
  // Order: explicit initialCalendarDate → minDate → maxDate → today.
  const fallbackCalendarDate =
    initialCalendarDate ??
    minDate ??
    maxDate ??
    (() => {
      const now = DateTime.now().setZone(zone);
      return { year: now.year, month: now.month - 1, day: now.day };
    })();

  return (
    <div
      className={classNames(styles.dateTimePicker, {
        [styles.invalid]: isInvalid,
        [styles.readOnly]: readOnly,
      })}
      title={t("dateTimeInput.projectTimezoneTitle", { timezone: zone })}
    >
      <SelectList<string>
        rootClassName={styles.calendarInput}
        listClassName={styles.calendar}
        isDisabled={readOnly}
      >
        {({ select }) => ({
          input: (
            <div className={styles.input}>
              {isValid ? (
                <p className={styles.value}>{display.toFormat("dd/MM/yyyy")}</p>
              ) : (
                <p className={styles.placeholder}>{placeholder}</p>
              )}
            </div>
          ),
          list: (
            <Calendar
              date={
                isValid
                  ? {
                      year: display.year,
                      month: display.month - 1,
                      day: display.day,
                    }
                  : fallbackCalendarDate
              }
              minDate={minDate}
              maxDate={maxDate}
              editDate={({ year, month, day }) => {
                if (readOnly) return;
                const base = isValid ? display : DateTime.now().setZone(zone);
                const iso = commitDateTime(
                  year,
                  month + 1,
                  day,
                  base.hour,
                  base.minute,
                );
                if (!iso) return;
                // Re-emit through select() when the day changed (matches
                // pre-migration task/event behavior — keeps the dropdown's
                // last-emitted state in sync with the new date).
                input(
                  isValid && display.day !== day && dateTime !== iso
                    ? select(iso)
                    : iso,
                );
              }}
              clear={
                clear
                  ? () => {
                      clear();
                      select("");
                    }
                  : undefined
              }
              today={
                today
                  ? () => {
                      today();
                      select("");
                    }
                  : undefined
              }
            />
          ),
        })}
      </SelectList>
      <SelectList<string> listClassName={styles.timeListWrapper}>
        {({ select }) => ({
          input: (
            <div className={styles.input}>
              <input
                type="text"
                className={classNames(
                  styles.timeInput,
                  isValid || selectAllDay ? styles.value : styles.placeholder,
                )}
                value={timeText}
                readOnly={readOnly}
                onChange={(e) => setTimeText(e.target.value)}
                onBlur={() => !readOnly && commitTime(timeText)}
                onKeyDown={(e) =>
                  e.key === "Enter" && !readOnly && commitTime(timeText)
                }
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
            </div>
          ),
          list: (
            <ul className={styles.timeList}>
              {Object.keys(times).map((key) => {
                const time = times[key as keyof typeof times];
                return (
                  <li
                    key={key}
                    className={styles.time}
                    onClick={() => {
                      if (readOnly) return;
                      const base = isValid
                        ? display
                        : DateTime.now().setZone(zone);
                      const iso = commitDateTime(
                        base.year,
                        base.month,
                        base.day,
                        time.hours,
                        time.minutes,
                      );
                      if (!iso) return;
                      setSelectAllDay?.(false);
                      input(select(iso));
                    }}
                  >
                    <p className={styles.timeName}>{key}</p>
                  </li>
                );
              })}
            </ul>
          ),
        })}
      </SelectList>
    </div>
  );
}

export { DateTimePicker };
